import { useState, useEffect } from 'react';
import { Panel } from '../Panel';
import Checkbox from '../../../../atoms/Checkbox/Checkbox';
import Button from '../../../../atoms/Button/Button';
import Box from '../../../../atoms/Box/Box';
import { TableColumn } from '../../types';
import styles from './ManageColumns.module.scss';
import Typography from '../../../../atoms/Typography/Typography';

export type ManageColumnsProps<T = Record<string, unknown>> = {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback when the panel is closed */
  onClose: () => void;
  /** All columns in the table */
  columns: TableColumn<T>[];
  /** Current visibility state for each column */
  columnVisibility: Record<string, boolean>;
  /** Callback when column visibility changes */
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  /** Theme for the panel */
  theme?: 'light' | 'dark';
  /** Test ID for the panel */
  dataTestId?: string;
  /** i18n labels for localization */
  labels?: {
    /** Panel title - defaults to "Manage Columns" */
    title?: string;
    /** Select all button label */
    selectAll?: string;
    /** Clear all button label */
    clearAll?: string;
    /** Summary text - use {{visible}}, {{total}} placeholders */
    columnsVisible?: string;
    /** Save button label - defaults to "Save" */
    save?: string;
  };
};

/**
 * Panel component for managing column visibility in a Table.
 * Changes are only applied when the Save button is clicked.
 * Closing the panel without saving discards changes.
 *
 * @component
 * @param {ManageColumnsProps} props - Component props
 * @returns {React.ReactElement} The ManageColumns component
 * @example
 * <ManageColumns
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   columns={columns}
 *   columnVisibility={visibility}
 *   onColumnVisibilityChange={setVisibility}
 * />
 */
export const ManageColumns = <T extends Record<string, unknown>>({
  isOpen,
  onClose,
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  theme = 'dark',
  dataTestId = 'manage-columns-panel',
  labels,
}: ManageColumnsProps<T>) => {
  // Local state for pending changes
  const [localVisibility, setLocalVisibility] = useState<Record<string, boolean>>(columnVisibility);

  // Reset local state when panel opens or external state changes
  useEffect(() => {
    if (isOpen) {
      setLocalVisibility(columnVisibility);
    }
  }, [isOpen, columnVisibility]);

  const handleColumnToggle = (columnKey: string, checked: boolean) => {
    setLocalVisibility((prev) => ({
      ...prev,
      [columnKey]: checked,
    }));
  };

  const handleSelectAll = () => {
    const newVisibility: Record<string, boolean> = { ...localVisibility };
    columns.forEach((col) => {
      if (col.hideable !== false) {
        newVisibility[col.key] = true;
      }
    });
    setLocalVisibility(newVisibility);
  };

  const handleClearAll = () => {
    const newVisibility: Record<string, boolean> = { ...localVisibility };
    columns.forEach((col) => {
      if (col.hideable !== false) {
        newVisibility[col.key] = false;
      }
    });
    // Keep at least one column visible (the first non-hideable or first column)
    const firstVisibleKey = columns.find((col) => col.hideable === false)?.key ?? columns[0]?.key;
    if (firstVisibleKey) {
      newVisibility[firstVisibleKey] = true;
    }
    setLocalVisibility(newVisibility);
  };

  const handleSave = () => {
    onColumnVisibilityChange(localVisibility);
    onClose();
  };

  const hideableColumns = columns.filter((col) => col.hideable !== false);
  const visibleCount = hideableColumns.filter((col) => localVisibility[col.key] !== false).length;

  return (
    <Panel
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      saveLabel={labels?.save ?? 'Save'}
      title={labels?.title ?? 'Manage Columns'}
      theme={theme}
      width="320px"
      dataTestId={dataTestId}
    >
      <Box className={styles.content} data-testid={`${dataTestId}-content`}>
        <Box className={styles.actions}>
          <Button
            label={labels?.selectAll ?? 'Select All'}
            handleClick={handleSelectAll}
            type="tertiary"
            size="small"
            gaComponent="manage-columns-select-all"
            dataTestId={`${dataTestId}-select-all`}
            theme={theme}
          />
          <Button
            label={labels?.clearAll ?? 'Clear All'}
            handleClick={handleClearAll}
            type="tertiary"
            size="small"
            gaComponent="manage-columns-clear-all"
            dataTestId={`${dataTestId}-clear-all`}
            theme={theme}
          />
        </Box>
        <Typography
          variant="label"
          size="small"
          className={styles.summary}
          theme={theme}
          color="white"
        >
          {(labels?.columnsVisible ?? '{{visible}} of {{total}} columns visible')
            .replace('{{visible}}', String(visibleCount))
            .replace('{{total}}', String(hideableColumns.length))}
        </Typography>
        <Box className={styles.columnList}>
          {columns.map((column) => {
            const isHideable = column.hideable !== false;
            const isVisible = localVisibility[column.key] !== false;

            return (
              <Box key={column.key} className={styles.columnItem}>
                <Checkbox
                  isChecked={isVisible}
                  isDisabled={!isHideable}
                  handleChange={(checked: boolean) => handleColumnToggle(column.key, checked)}
                  label={column.title}
                  theme={theme}
                  gaComponent="manage-columns-checkbox"
                  dataTestId={`${dataTestId}-column-${column.key}`}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    </Panel>
  );
};

ManageColumns.displayName = 'ManageColumns';
